import React, { useMemo, useState } from 'react';
import { H2, Icon, Select, Label } from '@thinkific/toga-react';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames/bind';

import { DateTime } from 'luxon';

import { LIVE_EVENTS_LIST_QUERY } from 'schema/Event/queries';

import {
  LiveEventsQuery,
  LiveEventsQueryVariables,
  EventStatusTypes,
  OrderDirection,
} from '__generated__/graphql/legacy/graphql';
import { genericToastAlert } from 'utils/toastHelpers';
import { goToNewEvent } from 'utils/routerHelpers';
import { isAdmin } from 'utils/userHelpers';
import { useCommunityContext } from 'components/CommunityContext';
import Button, { ButtonProps } from 'koba/components/Button';
import EventCard from 'components/Events/components/EventCard/EventCard';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import { EventsPageEmailNotificationUpsell } from 'components/Upsells/EmailNotification';
import FeatureCloak from 'components/VisibilityHelpers/FeatureCloak';

import EventsSkeleton from 'components/Events/components/EventsSkeleton/EventsSkeleton';
import NoEventEmptyState from 'components/Events/components/EmptyState/components/NoEventEmptyState';
import EventCardInteractionWrapper from 'components/Events/components/EventCardInteractionWrapper';
import { useWindowSize } from 'hooks/useWindowSize';
import { SCREEN_LG } from 'utils/constants';
import useToggle from 'hooks/useToggle';

import { features } from 'constants/featureFlags';
import styles from './EventListPage.module.scss';

const cx = classNames.bind(styles);

const EventListPage: React.FC = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const { community, currentUser, trackEvent } = useCommunityContext();
  const { width } = useWindowSize();
  const isMobile = width < SCREEN_LG;
  const isAdminUser = isAdmin(currentUser);

  const [filterByOption, setFilterByOption] = useState(0);
  const { activeItem: eventStatus, handleToggle } = useToggle<EventStatusTypes>(
    EventStatusTypes.Upcoming,
    () => setFilterByOption(filterByOptions[0].value)
  );

  const isUpcomingEvents = eventStatus === EventStatusTypes.Upcoming;
  const filterByOptions = [
    {
      value: 0,
      text: isUpcomingEvents
        ? t(
            'components-events-eventListPage-all_upcoming_events',
            'All upcoming events'
          )
        : t(
            'components-events-eventListPage-all_past_events',
            'All past events'
          ),
    },
    {
      value: 14,
      text: isUpcomingEvents
        ? t(
            'components-events-eventListPage-next_14_days_events',
            'Next 14 days'
          )
        : t(
            'components-events-eventListPage-past_14_days_events',
            'Past 14 days'
          ),
    },
    {
      value: 30,
      text: isUpcomingEvents
        ? t(
            'components-events-eventListPage-next_30_days_events',
            'Next 30 days'
          )
        : t(
            'components-events-eventListPage-past_30_days_events',
            'Past 30 days'
          ),
    },
  ];

  const startDateTime = useMemo(
    () =>
      isUpcomingEvents
        ? DateTime.now().toUTC().toString()
        : DateTime.now().minus({ days: filterByOption }).toUTC().toString(),
    [filterByOption, isUpcomingEvents]
  );
  const endDateTime = useMemo(
    () =>
      isUpcomingEvents
        ? DateTime.now().plus({ days: filterByOption }).toUTC().toString()
        : DateTime.now().toUTC().toString(),
    [filterByOption, isUpcomingEvents]
  );

  const { loading, data } = useQuery<LiveEventsQuery, LiveEventsQueryVariables>(
    LIVE_EVENTS_LIST_QUERY,
    {
      variables: {
        eventSourceId: community.id,
        eventStatus,
        sortDirection: isUpcomingEvents
          ? OrderDirection.Asc
          : OrderDirection.Desc,
        startDateTime: filterByOption ? startDateTime : null,
        endDateTime: filterByOption ? endDateTime : null,
      },
      fetchPolicy: 'cache-first',
      onError: () => {
        genericToastAlert();
      },
    }
  );

  const goToNewEventPage = (e) => {
    e.preventDefault();
    trackEvent(TrackingEvent.CLICK_CREATE_LIVE_EVENT, {
      [TrackingProperty.COMPONENT]: 'EventsPage',
    });

    goToNewEvent(router, community.id);
  };

  const setButtonProps = (isActive: boolean): Partial<ButtonProps> => {
    if (isActive) {
      return {
        isActive: true,
        color: community.primaryColor,
        appearance: 'default',
      };
    }

    return {
      isActive: false,
      appearance: 'ghost',
    };
  };

  const events = data?.site?.liveEvents;
  const eventsEdges = events?.edges;

  const createEventButton = isAdminUser && (
    <Button
      appearance="default"
      color={community.primaryColor}
      dataQA="create-event__button"
      href={`/communities/${community.id}/events/new/`}
      rel="noopener"
      onClick={goToNewEventPage}
    >
      {t('shared-CREATE_EVENT', 'CREATE EVENT')}
    </Button>
  );

  return (
    <>
      <>
        {!isMobile && (
          <div className={cx('events__header')}>
            <div className={cx('events__title')}>
              <Icon name="system-calendar" aria-hidden />
              <H2>{t('pages-events_title', 'Events')}</H2>
            </div>
            {createEventButton}
          </div>
        )}

        <>
          <div className={cx('events__filters-wrapper')}>
            <div className={cx('events__filters-header')}>
              <div className={cx('events__filters')}>
                <Button
                  appearance="ghost"
                  className={cx('events__filters-upcoming')}
                  dataQA="upcoming__button"
                  onClick={() => handleToggle(EventStatusTypes.Upcoming)}
                  {...setButtonProps(isUpcomingEvents)}
                >
                  {t('common-UPCOMING', 'UPCOMING')}
                </Button>
                <Button
                  appearance="ghost"
                  className={cx('events__filters-past')}
                  dataQA="past__button"
                  onClick={() => handleToggle(EventStatusTypes.Expired)}
                  {...setButtonProps(eventStatus === EventStatusTypes.Expired)}
                >
                  {t('common-PAST', 'PAST')}
                </Button>
              </div>
              {isMobile && <div>{createEventButton}</div>}
            </div>
            <div>
              <Label>{t('common-filter_by', 'Filter by')}</Label>
              <Select
                dataQA="events__filter-by"
                options={filterByOptions}
                selectLabel={
                  filterByOptions?.find((opt) => filterByOption === opt.value)
                    ?.text || ''
                }
                wrapperClass={cx('events__filters-filter-by')}
                onSelect={(value) => {
                  setFilterByOption(value);
                }}
              />
            </div>
          </div>
          {!loading ? (
            <>
              {eventsEdges?.length ? (
                <>
                  <FeatureCloak
                    communityEventNotifications={false}
                    flags={[
                      features.COMMUNITIES_EVENTS_RSVP,
                      features.COMMUNITIES_EVENT_EMAILS,
                    ]}
                    admins
                  >
                    <EventsPageEmailNotificationUpsell />
                  </FeatureCloak>
                  <div className={cx('events__list')} data-qa="events__list">
                    {eventsEdges?.map((edge) => {
                      const node = edge?.node;

                      if (!node) return null;

                      return (
                        <EventCardInteractionWrapper
                          dataQA="events__list-card"
                          eventId={node.id}
                          eventStatus={eventStatus}
                          key={node.id}
                        >
                          <EventCard
                            endTime={node.endTime}
                            id={node.id}
                            imageAltText={node.imageAltText || ''}
                            imageUrl={node.imageUrl || ''}
                            interestedUserCount={
                              node.interestedUsers?.totalCount
                            }
                            isInterested={node.isInterested || false}
                            startTime={node.startTime}
                            timezone={currentUser?.profile?.timezone}
                            title={node.title || ''}
                          />
                        </EventCardInteractionWrapper>
                      );
                    })}
                  </div>
                </>
              ) : (
                <NoEventEmptyState eventStatus={eventStatus} />
              )}
            </>
          ) : (
            <EventsSkeleton renderHeader={false} />
          )}
        </>
      </>
    </>
  );
};

export default EventListPage;
